import { AssignmentTurnedIn } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import CustomAutocomplete from 'lib/designSystem/CustomAutocomplete';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  assignCostCenterAndFinancialNode,
  getCostCenters,
  getFinancialNodes,
} from '../../../../../../../lib/api/accounting';

interface CostCenter {
  id: number;
  name: string;
  code: string;
}

interface FinancialNode {
  id: number;
  key: string;
  accountCode: string;
}

export interface Document {
  id: number;
  folio: string;
  businessIdentifier?: string;
  stakeholderIdentifier?: string;
  businessName?: string;
  stakeholderName?: string;
  subType: string;
  amount: number;
  costCenterId: number | null;
  financialNodeId: number | null;
  centralizationDate?: string | null;
  otherTaxesFinancialNodeId?: number | null;
  otherTaxesAmount?: number;
  business: {
    id: number;
    identifier: string;
    businessFinancialStatementNodes?: Array<{
      id: number;
      businessIdentifier: string;
      financialStatementsTreeNodesId: number;
      financialStatementsTreeNodes: {
        id: number;
        key: string;
        accountCode?: string;
      };
    }>;
  };
}

interface AssignmentData {
  documentId: number;
  costCenterId: number | null;
  financialNodeId: number | null;
  centralizationDate: string | null;
  otherTaxesFinancialNodeId: number | null;
}

interface AssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  documents: Document[];
  onAssignmentComplete: () => void;
}
const useStyles = makeStyles(() =>
  createStyles({
    iconButton: {
      padding: '6px',
      margin: '0 2px',
      borderRadius: '6px',
      '&:hover': {
        backgroundColor: 'rgba(25, 118, 210, 0.08)',
      },
    },

    assignButton: {
      color: '#28a745',
      '&:hover': {
        backgroundColor: 'rgba(40, 167, 69, 0.08)',
      },
    },

    actionsCell: {
      minWidth: '80px',
      maxWidth: '100px',
      padding: '8px !important',
    },
  }),
);
const AssignmentDialog: React.FC<AssignmentDialogProps> = ({
  open,
  onClose,
  documents,
  onAssignmentComplete,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [financialNodes, setFinancialNodes] = useState<FinancialNode[]>([]);
  const [assignments, setAssignments] = useState<AssignmentData[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [businessAssignedAccounts, setBusinessAssignedAccounts] = useState<{
    [key: string]: FinancialNode[];
  }>({});

  const getDocumentById = (id: number) => {
    return documents.find((doc) => doc.id === id);
  };

  const extractBusinessAccountsFromDocuments = (): {
    [key: string]: FinancialNode[];
  } => {
    return documents.reduce<{ [key: string]: FinancialNode[] }>(
      (accountsMap, doc) => {
        if (
          !doc.businessIdentifier ||
          !doc.business?.businessFinancialStatementNodes ||
          accountsMap[doc.businessIdentifier]
        ) {
          return accountsMap;
        }

        const nodes = doc.business.businessFinancialStatementNodes.map(
          (node) => ({
            id: node.financialStatementsTreeNodes.id,
            key: node.financialStatementsTreeNodes.key,
            accountCode: node.financialStatementsTreeNodes.accountCode || '',
          }),
        );

        accountsMap[doc.businessIdentifier] = nodes;
        return accountsMap;
      },
      {},
    );
  };

  // Inicializar assignments y extraer cuentas asignadas cuando se abre el diálogo
  useEffect(() => {
    if (open && documents.length > 0) {
      const initialAssignments = documents.map((doc) => ({
        documentId: doc.id,
        costCenterId: doc.costCenterId,
        financialNodeId: doc.financialNodeId,
        centralizationDate: doc.centralizationDate || null,
        otherTaxesFinancialNodeId: doc.otherTaxesFinancialNodeId || null,
      }));

      const accountsMap = extractBusinessAccountsFromDocuments();
      setBusinessAssignedAccounts(accountsMap);

      const assignmentsWithAutoSelection = initialAssignments.map(
        (assignment) => {
          if (assignment.financialNodeId) {
            return assignment;
          }

          const document = getDocumentById(assignment.documentId);
          if (!document?.businessIdentifier) {
            return assignment;
          }

          const assignedAccounts = accountsMap[document.businessIdentifier];
          if (assignedAccounts && assignedAccounts.length > 0) {
            return {
              ...assignment,
              financialNodeId: assignedAccounts[0].id,
            };
          }

          return assignment;
        },
      );

      setAssignments(assignmentsWithAutoSelection);
    }
  }, [open, documents]);

  // Cargar centros de costos y financial nodes
  useEffect(() => {
    if (open) {
      setLoading(true);
      Promise.all([getCostCenters(dispatch), getFinancialNodes(dispatch)])
        .then(([costCentersData, financialNodesData]) => {
          setCostCenters(costCentersData);
          setFinancialNodes(financialNodesData);
        })
        .catch((error) => {
          console.error('Error loading data:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, dispatch]);

  const handleAssignmentChange = (
    documentId: number,
    field:
      | 'costCenterId'
      | 'financialNodeId'
      | 'centralizationDate'
      | 'otherTaxesFinancialNodeId',
    value: number | null | string,
  ) => {
    setAssignments((prev) =>
      prev.map((assignment) =>
        assignment.documentId === documentId
          ? { ...assignment, [field]: value }
          : assignment,
      ),
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Filtrar solo las asignaciones que tienen al menos un valor
      const validAssignments = assignments.filter(
        (assignment) =>
          assignment.costCenterId !== null ||
          assignment.financialNodeId !== null ||
          assignment.centralizationDate !== null,
      );

      if (validAssignments.length === 0) {
        alert(
          'Debe asignar al menos un centro de costos, cuenta contable o fecha de centralización',
        );
        return;
      }

      await assignCostCenterAndFinancialNode(dispatch, validAssignments);

      onAssignmentComplete();
      onClose();
    } catch (error) {
      console.error('Error saving assignments:', error);
      alert('Error al guardar las asignaciones');
    } finally {
      setSaving(false);
    }
  };

  const getCostCenterById = (id: number | null): CostCenter | null => {
    if (!id) return null;
    return costCenters.find((cc) => cc.id === id) || null;
  };

  const getFinancialNodeById = (
    id: number | null,
    documentId?: number,
  ): FinancialNode | null => {
    if (!id) return null;
    if (documentId) {
      const document = getDocumentById(documentId);
      if (document?.businessIdentifier) {
        const assignedAccounts =
          businessAssignedAccounts[document.businessIdentifier];
        if (assignedAccounts) {
          const found = assignedAccounts.find((node) => node.id === id);
          if (found) return found;
        }
      }
    }
    return financialNodes.find((fn) => fn.id === id) || null;
  };

  const getFinancialNodesForDocument = (
    documentId: number,
  ): FinancialNode[] => {
    const document = getDocumentById(documentId);

    const assignedAccounts = document?.businessIdentifier
      ? businessAssignedAccounts[document.businessIdentifier] || []
      : [];

    if (financialNodes.length === 0) {
      return assignedAccounts;
    }

    // Si no hay businessIdentifier, retornar todas las cuentas normalmente
    if (!document?.businessIdentifier) {
      return financialNodes;
    }

    const assignedIds = new Set(assignedAccounts.map((acc) => acc.id));

    // Combinar: primero las asignadas, luego las demás (excluyendo duplicados)
    const otherNodes = financialNodes.filter(
      (node) => !assignedIds.has(node.id),
    );

    return [...assignedAccounts, ...otherNodes];
  };

  // Función de filtrado para CustomAutocomplete (solo para financialNodes)
  const filterFinancialNodes = (
    options: FinancialNode[],
    inputValue: string,
  ) => {
    if (!inputValue.trim()) return options;
    const searchText = inputValue.toLowerCase().trim();
    return options.filter(
      (option) =>
        option.key.toLowerCase().includes(searchText) ||
        (option.accountCode &&
          option.accountCode.toLowerCase().includes(searchText)),
    );
  };

  const toDateInputValue = (value: string | null) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 10);
  };
  const assignConditionsForAllDocumentsBelow = (documentId: number) => {
    const currentAssignment = assignments.find(
      (assignment) => assignment.documentId === documentId,
    );

    if (!currentAssignment) return;

    const currentIndex = assignments.findIndex(
      (assignment) => assignment.documentId === documentId,
    );

    if (currentIndex === -1) return;

    setAssignments((prev) =>
      prev.map((assignment, index) => {
        if (index > currentIndex) {
          return {
            ...assignment,
            costCenterId: currentAssignment.costCenterId,
            financialNodeId: currentAssignment.financialNodeId,
            centralizationDate: currentAssignment.centralizationDate,
            otherTaxesFinancialNodeId:
              currentAssignment.otherTaxesFinancialNodeId,
          };
        }
        return assignment;
      }),
    );
  };

  // Verificar si hay documentos con otherTaxesAmount > 0
  const hasOtherTaxes = documents.some(
    (doc) => doc.otherTaxesAmount && doc.otherTaxesAmount > 0,
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        Asignación de Centro de Costos y Cuenta Contable
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CenteredCircularProgress type="dialog" />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell>Folio</TableCell>
                  <TableCell>Rut</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Monto</TableCell>
                  <TableCell>Fecha de centralización</TableCell>
                  <TableCell>Centro de Costos</TableCell>
                  <TableCell>Cuenta Contable</TableCell>
                  {hasOtherTaxes && (
                    <TableCell>Cuenta Otros Impuestos </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {assignments.map((assignment) => {
                  const document = getDocumentById(assignment.documentId);
                  if (!document) return null;

                  return (
                    <TableRow key={assignment.documentId}>
                      <TableCell className={classes.actionsCell}>
                        <Box display="flex" justifyContent="start">
                          <LightTooltip
                            placement="right"
                            title={
                              <Typography style={{ padding: 12 }}>
                                Copiar centro de costos y cuenta contable a
                                todas las filas de abajo
                              </Typography>
                            }
                          >
                            <IconButton
                              className={classes.iconButton}
                              onClick={() =>
                                assignConditionsForAllDocumentsBelow(
                                  assignment.documentId,
                                )
                              }
                            >
                              <AssignmentTurnedIn
                                className={classes.assignButton}
                              />
                            </IconButton>
                          </LightTooltip>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {document.folio}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {document.businessIdentifier ||
                            document.stakeholderIdentifier}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {document.businessName || document.stakeholderName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {document.subType}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          ${document.amount.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="date"
                          size="small"
                          value={toDateInputValue(
                            assignment.centralizationDate,
                          )}
                          onChange={(event) =>
                            handleAssignmentChange(
                              assignment.documentId,
                              'centralizationDate',
                              event.target.value
                                ? `${event.target.value}T00:00:00.000Z`
                                : null,
                            )
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </TableCell>
                      <TableCell>
                        <Autocomplete
                          options={costCenters}
                          getOptionLabel={(option) =>
                            option.code
                              ? `${option.code} - ${option.name}`
                              : `${option.name}`
                          }
                          value={getCostCenterById(assignment.costCenterId)}
                          onChange={(_, newValue) =>
                            handleAssignmentChange(
                              assignment.documentId,
                              'costCenterId',
                              newValue?.id || null,
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              placeholder="Seleccionar centro de costos"
                            />
                          )}
                          sx={{ minWidth: 250 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ minWidth: 250 }}>
                          {(() => {
                            const financialNodesOptions =
                              getFinancialNodesForDocument(
                                assignment.documentId,
                              );
                            return financialNodesOptions.length > 0 ? (
                              <CustomAutocomplete<FinancialNode>
                                options={financialNodesOptions}
                                getOptionLabel={(option) =>
                                  option.accountCode
                                    ? `${option.accountCode} - ${option.key}`
                                    : `${option.key}`
                                }
                                value={getFinancialNodeById(
                                  assignment.financialNodeId,
                                  assignment.documentId,
                                )}
                                onChange={(newValue) =>
                                  handleAssignmentChange(
                                    assignment.documentId,
                                    'financialNodeId',
                                    newValue?.id || null,
                                  )
                                }
                                filterOptions={filterFinancialNodes}
                                placeholder="Seleccionar cuenta contable"
                              />
                            ) : (
                              <TextField
                                size="small"
                                placeholder="Cargando cuentas..."
                                disabled
                                sx={{ minWidth: 250 }}
                              />
                            );
                          })()}
                        </Box>
                      </TableCell>
                      {hasOtherTaxes && (
                        <TableCell>
                          {document.otherTaxesAmount &&
                          document.otherTaxesAmount > 0 ? (
                            <Box sx={{ minWidth: 250 }}>
                              {(() => {
                                const financialNodesOptions =
                                  getFinancialNodesForDocument(
                                    assignment.documentId,
                                  );
                                return financialNodesOptions.length > 0 ? (
                                  <CustomAutocomplete<FinancialNode>
                                    options={financialNodesOptions}
                                    getOptionLabel={(option) =>
                                      option.accountCode
                                        ? `${option.accountCode} - ${option.key}`
                                        : `${option.key}`
                                    }
                                    value={getFinancialNodeById(
                                      assignment.otherTaxesFinancialNodeId,
                                      assignment.documentId,
                                    )}
                                    onChange={(newValue) =>
                                      handleAssignmentChange(
                                        assignment.documentId,
                                        'otherTaxesFinancialNodeId',
                                        newValue?.id || null,
                                      )
                                    }
                                    filterOptions={filterFinancialNodes}
                                    placeholder="Seleccionar cuenta contable"
                                  />
                                ) : (
                                  <TextField
                                    size="small"
                                    placeholder="Cargando cuentas..."
                                    disabled
                                    sx={{ minWidth: 250 }}
                                  />
                                );
                              })()}
                            </Box>
                          ) : (
                            <Box sx={{ minWidth: 250 }} />
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <CustomButton onClick={onClose} disabled={saving} color="secondary">
          Cancelar
        </CustomButton>
        <CustomButton
          onClick={handleSave}
          color="primary"
          disabled={saving || loading}
        >
          {saving ? <CircularProgress size={20} /> : 'Guardar'}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default AssignmentDialog;
